/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './silo-details.scss';

/* eslint-disable import/no-unresolved, import/default */

import siloDetailsTemplate from './silo-details.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.siloDetails', [])
    .directive('agSiloDetails', SiloDetails)
    .name;

/*@ngInject*/
function SiloDetails() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            telemetryType: '<'
        },
        controller: SiloDetailsController,
        controllerAs: 'vm',
        templateUrl: siloDetailsTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function SiloDetailsController($scope, $filter, $translate, types, agTypes, agUtils, siloService, unitService, userService) {

    let vm = this;

    vm.agTypes = agTypes;
    vm.resolveIcon = resolveIcon;
    vm.loading = false;
    vm.historyFlotLoaded = false;

    vm.siloTelemetryHistoryLegendConfig = {
        displayMaxThreshold: true
    };

    vm.siloDeltaTelemetryHistoryLegendConfig = {
        displayMaxThreshold: true
    };

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.siloId = null;
    vm.silo = null;
    vm.siloData = null;

    if (vm.telemetryType === agTypes.telemetry.type.temperature) {
        vm.telemetryHistoryUnits = unitService.temperatureUnitSymbol();
        vm.siloTelemetryHistoryLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-temperature-threshold');
        vm.siloDeltaTelemetryHistoryLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-delta-temperature-threshold');
        vm.telemetryKeys = [agTypes.telemetry.silo.currentMinTemperature, agTypes.telemetry.silo.currentAvgTemperature, agTypes.telemetry.silo.currentMaxTemperature];
        vm.telemetryDeltaKeys = [agTypes.telemetry.silo.currentMinDeltaTemperature, agTypes.telemetry.silo.currentAvgDeltaTemperature, agTypes.telemetry.silo.currentMaxDeltaTemperature];
        vm.telemetryLabel = $translate.instant('ag.temperature.temperature') + ` - ${unitService.temperatureUnitSymbol()}`;
        vm.deltaTelemetryLabel = $translate.instant('ag.temperature.delta-temperature') + ` - ${unitService.temperatureUnitSymbol()}`;
        vm.tabTelemetryLabel = $translate.instant('ag.silo.current-temperatures');
        vm.tabDeltaTelemetryLabel = $translate.instant('ag.silo.delta-temperatures');
        vm.telemetryPrefix = agTypes.telemetry.sensorLines.temperaturePrefix;
        vm.deltaTelemetryPrefix = agTypes.telemetry.sensorLines.deltaTemperaturePrefix;
        vm.helpPageAction = agUtils.createHelpActionConfig("agSilo");
    } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
        vm.telemetryHistoryUnits = unitService.moistureUnitSymbol();
        vm.siloTelemetryHistoryLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-moisture-threshold');
        vm.siloDeltaTelemetryHistoryLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-delta-moisture-threshold');
        vm.telemetryKeys = [agTypes.telemetry.silo.currentMinMoisture, agTypes.telemetry.silo.currentAvgMoisture, agTypes.telemetry.silo.currentMaxMoisture];
        vm.telemetryDeltaKeys = [agTypes.telemetry.silo.currentMinDeltaMoisture, agTypes.telemetry.silo.currentAvgDeltaMoisture, agTypes.telemetry.silo.currentMaxDeltaMoisture];
        vm.telemetryLabel = $translate.instant('ag.moisture.moisture') + ` - ${unitService.moistureUnitSymbol()}`;
        vm.deltaTelemetryLabel = $translate.instant('ag.moisture.delta-moisture') + ` - ${unitService.moistureUnitSymbol()}`;
        vm.tabTelemetryLabel = $translate.instant('ag.moisture.moisture');
        vm.tabDeltaTelemetryLabel = $translate.instant('ag.moisture.delta-moisture');
        vm.telemetryPrefix = agTypes.telemetry.sensorLines.moisturePrefix;
        vm.deltaTelemetryPrefix = agTypes.telemetry.sensorLines.deltaMoisturePrefix;
        vm.helpPageAction = agUtils.createHelpActionConfig("agSilo", "ag.docsPageSections.silo-moisture-details");
    } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
        vm.telemetryHistoryUnits = unitService.co2UnitSymbol();
        vm.siloTelemetryHistoryLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-co2-threshold');
        vm.siloDeltaTelemetryHistoryLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-delta-co2-threshold');
        vm.telemetryKeys = [agTypes.telemetry.silo.currentMinCO2, agTypes.telemetry.silo.currentAvgCO2, agTypes.telemetry.silo.currentMaxCO2];
        vm.telemetryDeltaKeys = [agTypes.telemetry.silo.currentMinDeltaCO2, agTypes.telemetry.silo.currentAvgDeltaCO2, agTypes.telemetry.silo.currentMaxDeltaCO2];
        vm.telemetryLabel = $translate.instant('ag.co2.co2') + ` - ${unitService.co2UnitSymbol()}`;
        vm.deltaTelemetryLabel = $translate.instant('ag.co2.delta-co2') + ` - ${unitService.co2UnitSymbol()}`;
        vm.tabTelemetryLabel = $translate.instant('ag.co2.co2');
        vm.tabDeltaTelemetryLabel = $translate.instant('ag.co2.delta-co2');
        vm.telemetryPrefix = agTypes.telemetry.sensorLines.co2Prefix;
        vm.deltaTelemetryPrefix = agTypes.telemetry.sensorLines.deltaCO2Prefix;
        vm.helpPageAction = agUtils.createHelpActionConfig("agSilo", "ag.docsPageSections.silo-co2-details");
    }

    vm.onSensorDataHistory = onSensorDataHistory;
    vm.onSensorsData = onSensorsData;
    vm.onSensorSelected = onSensorSelected;
    vm.onFlotLoaded = onFlotLoaded;

    vm.selectedTableTabId = 0; // 0 = Main data, 1 = Delta Data
    vm.handleTableTabChange = handleTableTabChange;

    vm.selectedFlotTabId = 0; // 0 = Main data, 1 = Delta Data
    vm.handleFlotTabChange = handleFlotTabChange;

    function handleTableTabChange(tabId) {
        vm.selectedTableTabId = tabId;
    }

    function handleFlotTabChange(tabId) {
        vm.selectedFlotTabId = tabId;
    }

    function onFlotLoaded() {
        vm.historyFlotLoaded = true;
    }

    $scope.$watch('vm.ctx', function() {
        if (vm.ctx && vm.showData) {
            vm.ctx.widgetActions = [vm.helpPageAction];
            vm.ctx.hideTitlePanel = false;

            vm.siloId = null;
            if (vm.ctx.stateController) {
                vm.siloId = vm.ctx.stateController.getStateParams().entityId;
            }
            loadSilo();
            $scope.$watch(function() {
                    return vm.ctx.stateController.getStateParams().entityId;
                },
                function (newSiloId, prevSiloId) {
                    if (newSiloId && !angular.equals(newSiloId, prevSiloId)) {
                        vm.siloId = newSiloId;
                        vm.hideMoistureDetails = false;
                        vm.hideCO2Details = false;
                        loadSilo();
                    }
                }
            );

        }
    });

    $scope.$watch(function() { return vm.silo ? (vm.silo.label || vm.silo.name) : null; }, function(title) {
        if (vm.showData && vm.ctx && title) {
            if (vm.telemetryType === agTypes.telemetry.type.temperature) {
                vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-temperature-details') + ': ' + title;
            } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
                vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-moisture-details') + ': ' + title;
            } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
                vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-co2-details') + ': ' + title;
            }
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    function loadSilo() {
        vm.silo = null;
        vm.siloData = null;
        reset();
        if (vm.siloId) {
            vm.loading = true;
            siloService.getSilo(vm.siloId).then(
                function success(silo) {
                    vm.silo = silo;
                    if (!agTypes.siloType[vm.silo.siloType]) {
                        vm.silo.siloType = agTypes.siloType.cylinder;
                    }
                    if (vm.telemetryType === agTypes.telemetry.type.temperature) {
                        vm.maxSiloTelemetry = vm.silo.maxTemperature || 24;
                        vm.maxSiloTelemetryThreshold = unitService.toUserTemperature(vm.maxSiloTelemetry);
                        vm.maxSiloDeltaTelemetry = vm.silo.maxDeltaTemperature || 1;
                        vm.maxSiloDeltaTelemetryThreshold = unitService.toUserTemperature(vm.maxSiloDeltaTelemetry, true);
                    } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
                        vm.maxSiloTelemetry = vm.silo.maxMoisture || 24;
                        vm.maxSiloTelemetryThreshold = vm.maxSiloTelemetry;
                        vm.maxSiloDeltaTelemetry = vm.silo.maxDeltaMoisture || 1;
                        vm.maxSiloDeltaTelemetryThreshold = vm.maxSiloDeltaTelemetry;
                    } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
                        vm.maxSiloTelemetry = vm.silo.maxCO2 || 24;
                        vm.maxSiloTelemetryThreshold = vm.maxSiloTelemetry;
                        vm.maxSiloDeltaTelemetry = vm.silo.maxDeltaCO2 || 1;
                        vm.maxSiloDeltaTelemetryThreshold = vm.maxSiloDeltaTelemetry;
                    }
                    reloadSiloSensorLines();
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    }

    function reset() {
    }

    function onSensorsData(sensorsData) {
        $scope.$broadcast('onSiloSensorData', sensorsData);
    }

    function onSensorSelected(lineNum, sensorIndex) {
        $scope.$broadcast('onSiloSensorSelected', lineNum, sensorIndex);
    }

    function onSensorDataHistory(lineNum, sensorIndex) {
        vm.ctx.selectedLineNum = lineNum;
        vm.ctx.selectedSensorIndex = sensorIndex;
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            vm.ctx.selectedSensorTelemetry = agTypes.telemetry.type.temperature;
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            vm.ctx.selectedSensorTelemetry = agTypes.telemetry.type.moisture;
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            vm.ctx.selectedSensorTelemetry = agTypes.telemetry.type.co2;
        }
        vm.ctx.currentWidgetType = vm.agTypes.siloWidgetType.siloSensorsHistory;
    }

    function reloadSiloSensorLines() {
        if (vm.siloId) {
            siloService.getSiloSensorLines(vm.siloId).then(
                function success(sensorLines) {
                    if (vm.silo) {
                        siloService.excludeUnconnectedSensorLines(sensorLines);
                        var availableSensorLines = [];
                        if (vm.telemetryType === agTypes.telemetry.type.moisture) {
                            // NOTE: CO2 sensor lines can have moisture data as well so we include them also.
                            const moistSensorLines = $filter('filter')(sensorLines, {type: agTypes.deviceType.moistureSensorLines});
                            const co2SensorLines = $filter('filter')(sensorLines, {type: agTypes.deviceType.co2SensorLines});
                            availableSensorLines.push(...moistSensorLines);
                            availableSensorLines.push(...co2SensorLines);

                            if (!availableSensorLines.length) {
                                vm.hideMoistureDetails = true;
                            }
                        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
                            const co2SensorLines = $filter('filter')(sensorLines, {type: agTypes.deviceType.co2SensorLines});
                            availableSensorLines.push(...co2SensorLines);
                            if (!co2SensorLines.length) {
                                vm.hideCO2Details = true;
                            }
                        } else {
                            // NOTE: All cables can provide temperature data therefore we include all sensor lines.
                            availableSensorLines = sensorLines;
                        }

                        vm.sensorLines = $filter('orderBy')(availableSensorLines, agTypes.attributes.temperatureSensorLinesClient.lineNum);
                        vm.siloData = {
                            silo: vm.silo,
                            sensorLines: vm.sensorLines
                        };
                        updateSubscriptions();
                    }
                },
                function fail() {
                    vm.sensorLines = [];
                    vm.siloData = null;
                    updateSubscriptions();
                }
            );
        }
    }

    function updateSubscriptions() {
        vm.siloTelemetryHistorySubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.asset,
            entityId: vm.siloId.id,
            entityName: vm.silo.name,
            timeseries: []
        }];

        vm.siloDeltaTelemetryHistorySubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.asset,
            entityId: vm.siloId.id,
            entityName: vm.silo.name,
            timeseries: []
        }];

        for (var i = 0; i < 3; i++) {
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries.push({units: vm.telemetryHistoryUnits, decimals: 1});
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries.push({
                units: vm.telemetryHistoryUnits,
                decimals: 1
            });
        }

        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].name = agTypes.telemetry.silo.currentMinTemperature;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].label = $translate.instant('ag.silo.min-temperature');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].color = agTypes.colors.minColor;

            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].name = agTypes.telemetry.silo.currentAvgTemperature;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].label = $translate.instant('ag.silo.avg-temperature');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].color = agTypes.colors.avgColor;

            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].name = agTypes.telemetry.silo.currentMaxTemperature;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].label = $translate.instant('ag.silo.max-temperature');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].color = agTypes.colors.maxColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].name = agTypes.telemetry.silo.currentMinDeltaTemperature;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].label = $translate.instant('ag.silo.min-delta-temperature');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].color = agTypes.colors.minColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].name = agTypes.telemetry.silo.currentAvgDeltaTemperature;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].label = $translate.instant('ag.silo.avg-delta-temperature');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].color = agTypes.colors.avgColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].name = agTypes.telemetry.silo.currentMaxDeltaTemperature;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].label = $translate.instant('ag.silo.max-delta-temperature');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].color = agTypes.colors.maxColor;

            for (i = 0; i < 3; i++) {
                vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[i].postFuncBody = unitService.temperaturePostprocessingFuncBody();
                vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[i].postFuncBody = unitService.temperaturePostprocessingFuncBody(true);
            }

        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].name = agTypes.telemetry.silo.currentMinMoisture;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].label = $translate.instant('ag.silo.min-moisture');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].color = agTypes.colors.hasAlarmsColor;

            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].name = agTypes.telemetry.silo.currentAvgMoisture;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].label = $translate.instant('ag.silo.avg-moisture');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].color = agTypes.colors.normalGreyColor;

            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].name = agTypes.telemetry.silo.currentMaxMoisture;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].label = $translate.instant('ag.silo.max-moisture');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].color = agTypes.colors.minColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].name = agTypes.telemetry.silo.currentMinDeltaMoisture;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].label = $translate.instant('ag.silo.min-delta-moisture');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].color = agTypes.colors.hasAlarmsColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].name = agTypes.telemetry.silo.currentAvgDeltaMoisture;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].label = $translate.instant('ag.silo.avg-delta-moisture');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].color = agTypes.colors.normalGreyColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].name = agTypes.telemetry.silo.currentMaxDeltaMoisture;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].label = $translate.instant('ag.silo.max-delta-moisture');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].color = agTypes.colors.minColor;
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].name = agTypes.telemetry.silo.currentMinCO2;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].label = $translate.instant('ag.silo.min-co2');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[0].color = agTypes.colors.hasAlarmsColor;

            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].name = agTypes.telemetry.silo.currentAvgCO2;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].label = $translate.instant('ag.silo.avg-co2');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[1].color = agTypes.colors.normalGreyColor;

            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].name = agTypes.telemetry.silo.currentMaxCO2;
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].label = $translate.instant('ag.silo.max-co2');
            vm.siloTelemetryHistorySubscriptionsInfo[0].timeseries[2].color = agTypes.colors.minColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].name = agTypes.telemetry.silo.currentMinDeltaCO2;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].label = $translate.instant('ag.silo.min-delta-co2');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[0].color = agTypes.colors.hasAlarmsColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].name = agTypes.telemetry.silo.currentAvgDeltaCO2;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].label = $translate.instant('ag.silo.avg-delta-co2');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[1].color = agTypes.colors.normalGreyColor;

            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].name = agTypes.telemetry.silo.currentMaxDeltaCO2;
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].label = $translate.instant('ag.silo.max-delta-co2');
            vm.siloDeltaTelemetryHistorySubscriptionsInfo[0].timeseries[2].color = agTypes.colors.minColor;
        }
        vm.loading = false;
    }

    function resolveIcon(isDelta) {
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            return isDelta ? 'ag:delta_thermometer' : 'ag:thermometer';
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            return isDelta ? 'ag:delta_moisture' : 'ag:moisture';
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            return isDelta ? 'ag:delta_co2_level' : 'ag:co2_level';
        } else {
            return '';
        }
    }
}
